import type {UserSettingsOptionConfig} from '@github-ui/issue-create/getSafeConfig'

import styles from './MilestoneCreateEdit.module.css'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {MilestoneError} from './MilestoneError'
import {LABELS} from './constants/labels'
import {MilestoneForm} from './components/MilestoneForm'
import {graphql, useFragment} from 'react-relay'
import {goBack} from '@github-ui/history'
import {useCallback, useState, type Dispatch, type SetStateAction} from 'react'
import type {MilestoneEditFormRepositoryQuery$key} from './__generated__/MilestoneEditFormRepositoryQuery.graphql'
import type {CreateMilestoneInput} from './mutations/__generated__/createRepositoryMilestoneMutation.graphql'

type EditMilestoneInput = {
  id: string
  title: string
  description?: string | null
  dueOn?: string | null
}

type MilestoneEditProps = {
  repository: MilestoneEditFormRepositoryQuery$key
  optionConfig?: UserSettingsOptionConfig
}

export function MilestoneEdit(props: MilestoneEditProps) {
  const [submissionErrors, setSubmissionErrors] = useState<string | null>(null)

  const {repository} = props

  const repositoryData = useFragment(
    graphql`
      fragment MilestoneEditFormRepositoryQuery on Repository @argumentDefinitions(number: {type: "Int!"}) {
        viewerCanPush
        milestone(number: $number) {
          id
          title
          description
          dueOn
        }
        ...MilestoneFormRepositoryQueryInternal
      }
    `,
    repository,
  )

  const handleFormSubmit = useCallback(
    (input: CreateMilestoneInput, setIsSubmitting: Dispatch<SetStateAction<boolean>>) => {
      setSubmissionErrors(null)

      if (!repositoryData?.milestone) {
        setSubmissionErrors(LABELS.milestoneErrorMessage)
        setIsSubmitting(false)
        return
      }

      if (!repositoryData?.viewerCanPush) {
        setSubmissionErrors(LABELS.milestoneEditPermissionError)
        setIsSubmitting(false)
        return
      }

      // eslint-disable-next-line unused-imports/no-unused-vars
      const editInput: EditMilestoneInput = {
        id: repositoryData.milestone.id,
        ...input,
      }

      // TODO: Implement the mutation to update the milestone
    },
    [repositoryData],
  )

  // TODO check design
  if (!repositoryData?.milestone) {
    return (
      <div className={styles.middlePaneWrapper} data-hpc>
        <p>{LABELS.milestoneErrorMessage}</p>
      </div>
    )
  }

  const initialValues = {
    id: repositoryData.milestone.id,
    title: repositoryData.milestone.title,
    description: repositoryData.milestone.description,
    dueOn: repositoryData.milestone.dueOn,
  }

  const handleFormCancel = () => {
    goBack()
  }

  return (
    <div className={styles.middlePaneWrapper} data-hpc>
      <ErrorBoundary
        fallback={<MilestoneError title={LABELS.milestonePageError} message={LABELS.milestonePageErrorMessage} />}
      >
        <MilestoneForm
          repository={repositoryData}
          optionConfig={props.optionConfig}
          formTitle={LABELS.editMilestoneTitle}
          formSubmitLabel={LABELS.saveChanges}
          onSubmit={handleFormSubmit}
          onCancel={handleFormCancel}
          submissionErrors={submissionErrors}
          initialValues={initialValues}
        />
      </ErrorBoundary>
    </div>
  )
}

try{ MilestoneEdit.displayName ||= 'MilestoneEdit' } catch {}